import React, {useState, useMemo, useEffect} from "react";
import {Box, Paper, Stack, Typography, Chip, Button, Link, Tooltip} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {SeverityDot, StatusChip, HelpPopover, Identity} from "./cells";
import MiniBars from "./MiniBars";
import {AttentionRow, GroupRow} from "./AttentionPanel";
import {MoverRow, EventRow} from "./MovedPanel";
import {FEED_ANCHOR_ID, FEED_VISIBLE, FEED_HELP, feedDigest, silentLine} from "./feed";
import {
    fmtMoneyShort, fmtSignedMoney, fmtDay, fmtCompact, fmtPct0, reasonOf, shortProvider, STATUS_META, STATUS_WORD, severityOf, EMPTY,
    isNilValue,
} from "./format";
import {TYPE, TABULAR, NEUTRAL, SEV, ATTENTION_ROW_H, WARNING_TEXT} from "./tokens";

const barsWidthFor = (days) => (days > 14 ? 90 : 56);
const keyActivate = (fn) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } };
const CHIP_ORDER = ["DEAD", "NO_ADS", "STOPPED", "PRE_KILL", "WATCH", "FILTERED"];

/** Same 44 px two-line grid as the alarm rows (dot · tag · chip · identity · money · bars · chevron). */
const rowSx = (barsWidth, {selected, group} = {}) => ({
    display: "grid", gridTemplateColumns: `12px 44px 74px minmax(0,1fr) auto ${barsWidth + 4}px 16px`, gridTemplateRows: "20px 18px",
    alignItems: "center", columnGap: 1, height: ATTENTION_ROW_H, px: 1.5, cursor: "pointer", boxSizing: "border-box",
    borderBottom: `1px solid ${NEUTRAL.hairline}`,
    bgcolor: selected ? NEUTRAL.selected : group ? "rgba(0,0,0,.015)" : "transparent",
    "&:hover": {bgcolor: NEUTRAL.hover},
    "&:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing},
});

/** A partner with no rows for yesterday past its usual landing time: alarm grammar, `→ ?` in place of yesterday's figure. */
const SilentRow = ({item, days, onFilterProvider, onResync, asOf}) => {
    const barsWidth = barsWidthFor(days);
    const reason = `no data for yesterday · usually lands ${item.expected || EMPTY} UTC · last reported ${fmtDay(item.lastDay)}`;
    return (
        <Box role="button" tabIndex={0} aria-label={`${shortProvider(item.provider)} not reported, re-sync`}
             onClick={() => (onResync ? onResync(item.provider, asOf) : onFilterProvider && onFilterProvider(item.provider))}
             onKeyDown={keyActivate(() => (onResync ? onResync(item.provider, asOf) : onFilterProvider && onFilterProvider(item.provider)))}
             sx={rowSx(barsWidth, {})}>
            <Box sx={{gridRow: 1, gridColumn: 1, display: "flex", alignItems: "center"}}><SeverityDot status="WATCH"/></Box>
            <Typography component="span" sx={{...TYPE.tag, fontSize: 11.5, gridRow: 1, gridColumn: 2, whiteSpace: "nowrap"}}>{item.tags} tag{item.tags === 1 ? "" : "s"}</Typography>
            <Box sx={{gridRow: 1, gridColumn: 3, display: "flex", alignItems: "center"}}><StatusChip status="PARTIAL"/></Box>
            <Typography component="span" noWrap sx={{gridRow: 1, gridColumn: "4 / 6", fontSize: 12.5, minWidth: 0}} title={item.provider}>
                <Box component="span" sx={{color: "text.primary", fontWeight: 500}}>{shortProvider(item.provider)}</Box>
            </Typography>
            <Typography component="span" noWrap title={reason} sx={{gridRow: 2, gridColumn: "2 / 5", fontSize: 12, color: "text.secondary", minWidth: 0}}>
                <Box component="span" sx={{fontWeight: 700, color: "text.primary", mr: 0.5}}>new</Box>{reason}
            </Typography>
            <Typography component="span" sx={{gridRow: 2, gridColumn: 5, fontSize: 12, ...TABULAR, whiteSpace: "nowrap", color: "text.secondary", textAlign: "right"}}>
                usually {fmtMoneyShort(item.usual)}/d → <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>?</Box>
            </Typography>
            {onResync && <Typography component="span" sx={{gridRow: "1 / 3", gridColumn: "6 / 8", fontSize: 12, fontWeight: 600, color: "primary.main", textAlign: "right", whiteSpace: "nowrap"}}>re-sync</Typography>}
        </Box>
    );
};

/** A launch losing coverage (not yet in alarm): alarm grammar with a LAUNCH chip and the day-1 → last pace. */
const LaunchRow = ({item, days, selected, onSelect}) => {
    const barsWidth = barsWidthFor(days);
    const l = item.launch;
    const first = l.daily && l.daily[0];
    const reported = (l.daily || []).filter((d) => d.reported !== false);
    const last = reported.length ? reported[reported.length - 1] : first;
    const cov = (d) => (d && !isNilValue(d.coverage) ? fmtPct0(d.coverage) : EMPTY);
    const reason = `launch · day ${l.day_n} · coverage ${cov(first)} → ${cov(last)} with traffic ${last && first && last.fs >= 0.95 * first.fs ? "holding" : "down"}${l.pending ? " · ◌ last day not yet reported" : ""}`;
    return (
        <Box role="button" tabIndex={0} aria-label={`${l.tag} launch off pace, open details`}
             onClick={() => onSelect(l.tag)} onKeyDown={keyActivate(() => onSelect(l.tag))} sx={rowSx(barsWidth, {selected})}>
            <Box sx={{gridRow: 1, gridColumn: 1, display: "flex", alignItems: "center"}}><SeverityDot status="WATCH"/></Box>
            <Typography component="span" sx={{...TYPE.tag, gridRow: 1, gridColumn: 2}}>{l.tag}</Typography>
            <Box sx={{gridRow: 1, gridColumn: 3, display: "flex", alignItems: "center"}}>
                <Box component="span" sx={{display: "inline-flex", alignItems: "center", height: 18, px: 0.75, borderRadius: "4px", ...TYPE.chip, lineHeight: 1, bgcolor: (t) => SEV.warning.chipBg(t), color: WARNING_TEXT, border: "1px solid transparent"}}>LAUNCH</Box>
            </Box>
            <Identity site={l.site} provider={l.provider} sx={{gridRow: 1, gridColumn: "4 / 6"}}/>
            <Typography component="span" noWrap title={reason} sx={{gridRow: 2, gridColumn: "2 / 5", fontSize: 12, color: "text.secondary", minWidth: 0}}>
                <Box component="span" sx={{fontWeight: 700, color: "text.primary", mr: 0.5}}>new</Box>{reason}
            </Typography>
            <Typography component="span" sx={{gridRow: 2, gridColumn: 5, fontSize: 12, ...TABULAR, whiteSpace: "nowrap", color: "text.secondary", textAlign: "right"}}>
                day 1 {fmtMoneyShort(l.day1_rev)} → <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>{fmtMoneyShort(l.last_rev)}</Box>
            </Typography>
            <Box sx={{gridRow: "1 / 3", gridColumn: 6, display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                <MiniBars daily={l.daily || []} metric="coverage" width={barsWidth} height={18} gap={2} partialLast={l.pending} label={`${l.tag}: daily coverage`}/>
            </Box>
        </Box>
    );
};

/** One collapsed line (`+7 positive (+$62)` / `4 ongoing alarms · $53/d at risk` / `Recovered: gp13 …`) that expands to its rows. */
const FoldRow = ({label, detail, open, onToggle, tone = "secondary", ariaLabel}) => (
    <Box role="button" tabIndex={0} aria-expanded={open} aria-label={ariaLabel || label}
         onClick={onToggle} onKeyDown={keyActivate(onToggle)}
         sx={{display: "flex", alignItems: "center", gap: 1, height: 32, px: 1.5, cursor: "pointer", borderBottom: `1px solid ${NEUTRAL.hairline}`,
             "&:hover": {bgcolor: NEUTRAL.hover}, "&:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing}}}>
        <Box sx={{width: 12, display: "flex", alignItems: "center"}}>{tone === "alarm" && <SeverityDot status="WATCH" size={7} sx={{opacity: 0.6}}/>}</Box>
        <Typography component="span" noWrap sx={{fontSize: 12.5, fontWeight: 600, color: tone === "muted" ? "text.secondary" : "text.primary", ...TABULAR, minWidth: 0}}>{label}</Typography>
        {detail && <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", ...TABULAR, minWidth: 0, flex: "1 1 auto"}} title={detail}>{detail}</Typography>}
        {!detail && <Box sx={{flex: "1 1 auto"}}/>}
        <ExpandMoreIcon sx={{fontSize: 16, color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s"}}/>
    </Box>
);

/** Recovered tags: one grey line; expands to `gp13 back to $7 on Sep 13 after filtered` per tag. */
const RecoveredRows = ({rows, onSelect, selectedTag}) => rows.map((r) => (
    <Box key={r.tag} role="button" tabIndex={0} aria-label={`${r.tag} recovered, open details`} onClick={() => onSelect(r.tag)} onKeyDown={keyActivate(() => onSelect(r.tag))}
         sx={{display: "flex", alignItems: "center", gap: 1, height: 30, px: 1.5, pl: 3.5, cursor: "pointer", borderBottom: `1px solid ${NEUTRAL.hairline}`,
             bgcolor: selectedTag === r.tag ? NEUTRAL.selected : "transparent", "&:hover": {bgcolor: NEUTRAL.hover}, "&:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing}}}>
        <Typography component="span" sx={{...TYPE.tag, fontSize: 12.5}}>{r.tag}</Typography>
        <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", minWidth: 0}} title={reasonOf(r)}>{reasonOf(r)}</Typography>
        <Box sx={{flex: "1 1 auto"}}/>
        <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", minWidth: 0}}>{shortProvider(r.provider)}</Typography>
    </Box>
));

/**
 * "What happened yesterday" (v3 §2.2): one ranked feed replacing Needs attention + Moved. `feed` = buildFeed() output;
 * `counts` = status counts (chips filter the table); `overview` supplies the residual and the wasted traffic.
 */
/** Desktop right column: the open (already known) alarms as a card of their own — the biggest money figure must not sit behind a chevron. */
export const OpenAlarmsCard = ({item, days, onSelect, selectedTag, launches}) => {
    const [expanded, setExpanded] = useState(false);
    const [open, setOpen] = useState({});
    const launchByTag = useMemo(() => new Map((launches || []).map((l) => [l.tag, l])), [launches]);
    if (!item) return null;
    const shown = expanded ? item.items : item.items.slice(0, 6);
    const hidden = item.items.length - shown.length;
    return (
        <Paper variant="outlined" sx={{borderRadius: 1.5, p: 1.5, pt: 1.25, minWidth: 0}}>
            <Stack direction="row" alignItems="center" sx={{columnGap: 1, mb: 0.5}}>
                <Typography component="h2" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em"}}>Open alarms</Typography>
                <Typography component="span" sx={{fontSize: 13, fontWeight: 700, ...TABULAR, color: "text.secondary"}}>{item.n}</Typography>
                {item.atRisk > 0 && <Typography component="span" sx={{fontSize: 12, color: "text.secondary", ...TABULAR}}>· <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>{fmtMoneyShort(item.atRisk)}/d</Box> at risk · already known</Typography>}
            </Stack>
            <Box sx={{mx: -1.5, borderTop: `1px solid ${NEUTRAL.hairline}`}}>
                {shown.map((a) => (a.kind === "group"
                    ? <GroupRow key={a.key} group={{...a, launch: launchByTag.get(a.rows[0].tag) || null}} days={days} open={Boolean(open[a.key])} onToggle={() => setOpen((o) => ({...o, [a.key]: !o[a.key]}))} onSelect={onSelect} selectedTag={selectedTag} launches={launchByTag}/>
                    : <AttentionRow key={a.key} row={a.row} days={days} selected={selectedTag === a.row.tag} onSelect={onSelect} launch={launchByTag.get(a.row.tag) || null}/>))}
            </Box>
            {hidden > 0 && <Button size="small" onClick={() => setExpanded(true)} sx={{textTransform: "none", fontSize: 12, mt: 0.25, minHeight: 26, py: 0}}>Show {hidden} more</Button>}
        </Paper>
    );
};

const FeedPanel = ({feed, asOf, days, compareBase, counts, statuses, onToggleStatus, onSelect, selectedTag, overview, launches, onFilterProvider, onResync, lastSeen, earnedTags, pct, hideOngoing = false}) => {
    const [expanded, setExpanded] = useState(false);
    const [open, setOpen] = useState({});
    useEffect(() => { setExpanded(false); }, [feed]);
    const launchByTag = useMemo(() => new Map((launches || []).map((l) => [l.tag, l])), [launches]);
    const toggle = (k) => setOpen((o) => ({...o, [k]: !o[k]}));
    const items = hideOngoing ? feed.items.filter((it) => it.kind !== "ongoing") : feed.items;
    const shown = expanded ? items : items.slice(0, FEED_VISIBLE);
    const hidden = items.length - shown.length;
    const digest = feedDigest(feed.digest, overview && overview.residual_usd, overview && overview.n_quiet);
    const silent = silentLine(counts, overview && overview.wasted_sent_d1);
    const chips = CHIP_ORDER.filter((s) => counts[s]);
    const sinceVisit = lastSeen && asOf && lastSeen < asOf ? ` · since your last visit (${fmtDay(lastSeen)})` : "";

    const renderItem = (it) => {
        switch (it.kind) {
            case "alarm":
                return <AttentionRow key={it.key} row={it.row} days={days} selected={selectedTag === it.row.tag} onSelect={onSelect} isNew launch={launchByTag.get(it.row.tag) || null}/>;
            case "alarmGroup":
                return <GroupRow key={it.key} group={{...it, launch: launchByTag.get(it.rows[0].tag) || null}} days={days} open={Boolean(open[it.key])} onToggle={() => toggle(it.key)} onSelect={onSelect} selectedTag={selectedTag} isNew launches={launchByTag}/>;
            case "silent":
                return <SilentRow key={it.key} item={it} days={days} onFilterProvider={onFilterProvider} onResync={onResync} asOf={asOf}/>;
            case "launch":
                return <LaunchRow key={it.key} item={it} days={days} selected={selectedTag === it.tag} onSelect={onSelect}/>;
            case "event":
                return <EventRow key={it.key} item={it} days={days} compareBase={compareBase} asOf={asOf} open={Boolean(open[it.key])} onToggle={() => toggle(it.key)} onSelect={onSelect} selectedTag={selectedTag}/>;
            case "row":
                return <MoverRow key={it.key} item={it} days={days} compareBase={compareBase} asOf={asOf} selected={selectedTag === it.tag} onSelect={onSelect}/>;
            case "positives":
                return (
                    <React.Fragment key={it.key}>
                        <FoldRow label={`+${it.n} positive (${fmtSignedMoney(it.usd)})`} detail={it.items.slice(0, 4).map((m) => `${m.tag} ${fmtSignedMoney(m.usd)}`).join(" · ") + (it.n > 4 ? ` +${it.n - 4}` : "")}
                                 open={Boolean(open[it.key])} onToggle={() => toggle(it.key)} tone="muted" ariaLabel={`${it.n} positive movers, ${open[it.key] ? "collapse" : "expand"}`}/>
                        {open[it.key] && it.items.map((m) => <MoverRow key={m.key} item={m} days={days} compareBase={compareBase} asOf={asOf} selected={selectedTag === m.tag} onSelect={onSelect} child/>)}
                    </React.Fragment>
                );
            case "ongoing":
                return (
                    <React.Fragment key={it.key}>
                        <FoldRow label={`${it.n} ongoing alarm${it.n === 1 ? "" : "s"}`} detail={`${it.atRisk > 0 ? `${fmtMoneyShort(it.atRisk)}/d at risk · ` : ""}${it.items.slice(0, 5).map((a) => (a.kind === "group" ? `${a.rows.length} ${shortProvider(a.provider)}` : a.row.tag)).join(" · ")}${it.items.length > 5 ? ` +${it.items.length - 5}` : ""}`}
                                 open={Boolean(open[it.key])} onToggle={() => toggle(it.key)} tone="alarm" ariaLabel={`${it.n} ongoing alarms, ${open[it.key] ? "collapse" : "expand"}`}/>
                        {open[it.key] && it.items.map((a) => (a.kind === "group"
                            ? <GroupRow key={a.key} group={{...a, launch: launchByTag.get(a.rows[0].tag) || null}} days={days} open={Boolean(open[a.key])} onToggle={() => toggle(a.key)} onSelect={onSelect} selectedTag={selectedTag} launches={launchByTag}/>
                            : <AttentionRow key={a.key} row={a.row} days={days} selected={selectedTag === a.row.tag} onSelect={onSelect} launch={launchByTag.get(a.row.tag) || null}/>))}
                    </React.Fragment>
                );
            case "recovered":
                return (
                    <React.Fragment key={it.key}>
                        <FoldRow label={`${it.n} recovered`} detail={it.rows.slice(0, 4).map((r) => `${r.tag} ${reasonOf(r)}`).join(" · ")}
                                 open={Boolean(open[it.key])} onToggle={() => toggle(it.key)} tone="muted" ariaLabel={`${it.n} recovered tags, ${open[it.key] ? "collapse" : "expand"}`}/>
                        {open[it.key] && <RecoveredRows rows={it.rows} onSelect={onSelect} selectedTag={selectedTag}/>}
                    </React.Fragment>
                );
            default:
                return null;
        }
    };

    return (
        <Paper id={FEED_ANCHOR_ID} variant="outlined" sx={{borderRadius: 1.5, p: 1.5, pt: 1.25, display: "flex", flexDirection: "column", minWidth: 0, scrollMarginTop: 12}}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{columnGap: 1, rowGap: 0.5}}>
                <Typography component="h2" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary"}}>
                    What happened{asOf ? ` · ${fmtDay(asOf)}` : ""}
                </Typography>
                <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", ...TABULAR, minWidth: 0}} title={digest.map((p) => p.text).join(" · ") + sinceVisit}>
                    {digest.map((p, i) => (
                        <React.Fragment key={p.text}>
                            {i > 0 && " · "}
                            <Box component="span" sx={{fontWeight: p.strong ? 600 : 400, color: p.strong ? "text.primary" : "inherit"}}>{p.text}</Box>
                        </React.Fragment>
                    ))}
                    {sinceVisit}
                </Typography>
                {chips.map((s) => {
                    const sev = severityOf(s);
                    const active = statuses.includes(s);
                    return (
                        <Chip key={s} size="small" clickable onClick={() => onToggleStatus(s)} title={STATUS_META[s].hint}
                              label={`${counts[s]} ${STATUS_WORD[s]}`} aria-pressed={active}
                              icon={<SeverityDot status={s} size={7} sx={{ml: "6px !important"}}/>}
                              sx={{
                                  height: 22, fontSize: 11.5, fontWeight: 600, ...TABULAR,
                                  bgcolor: active ? (t) => SEV[sev].chipBg(t) : "transparent",
                                  border: "1px solid", borderColor: active ? (t) => SEV[sev].dot(t) : NEUTRAL.divider,
                                  color: active ? (t) => SEV[sev].chipText(t) : "text.secondary",
                                  "& .MuiChip-label": {pl: 0.5},
                              }}/>
                    );
                })}
                <Box sx={{flexGrow: 1}}/>
                <HelpPopover label="What happened" lines={FEED_HELP}/>
            </Stack>

            <Box sx={{mx: -1.5, mt: 0.75, borderTop: `1px solid ${NEUTRAL.hairline}`}}>
                {feed.quiet && (
                    <Typography component="div" sx={{fontSize: 13, color: "text.secondary", py: 1.25, px: 1.5, ...TABULAR}}>
                        <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>Nothing new broke</Box>
                        {!isNilValue(earnedTags) && <> · {earnedTags} tags earned</>}
                        {overview && overview.yesterday && !isNilValue(overview.yesterday.rev) && <> {fmtMoneyShort(overview.yesterday.rev)}{!isNilValue(pct) ? ` (p${pct} of 28 d)` : ""}</>}
                        {overview && overview.yesterday && Array.isArray(overview.yesterday.pending_providers) && (
                            <> · {overview.yesterday.pending_providers.length ? `◌ ${overview.yesterday.pending_providers.length} partner${overview.yesterday.pending_providers.length === 1 ? "" : "s"} not yet reported` : "all partners reported"}</>
                        )}
                    </Typography>
                )}
                {shown.map(renderItem)}
            </Box>
            {hidden > 0 && (
                <Button size="small" onClick={() => setExpanded(true)} sx={{alignSelf: "flex-start", textTransform: "none", fontSize: 12, mt: 0.25, minHeight: 26, py: 0}}>
                    Show {hidden} more
                </Button>
            )}
            {expanded && items.length > FEED_VISIBLE && (
                <Button size="small" onClick={() => setExpanded(false)} sx={{alignSelf: "flex-start", textTransform: "none", fontSize: 12, mt: 0.25, minHeight: 26, py: 0}}>Show fewer</Button>
            )}

            {(silent.total > 0 || silent.wasted) && (
                <Typography component="div" noWrap sx={{...TYPE.caption, mt: 0.75, pt: 1, borderTop: `1px solid ${NEUTRAL.hairline}`, ...TABULAR}}
                            title={`Outside the alarms: ${silent.parts.map((p) => p.text).join(" · ")}${silent.wasted ? ` · ${fmtCompact(silent.wasted)} searches sent yesterday to tags that paid $0` : ""}`}>
                    <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>Also</Box> · 
                    {silent.parts.map((p, i) => (
                        <React.Fragment key={p.status}>
                            {i > 0 && " · "}
                            <Tooltip title={STATUS_META[p.status].hint} placement="top" arrow>
                                <Link component="button" type="button" underline="hover" onClick={() => onToggleStatus(p.status)} aria-pressed={statuses.includes(p.status)}
                                      sx={{fontSize: "inherit", fontFamily: "inherit", color: statuses.includes(p.status) ? "primary.main" : "text.secondary", fontWeight: statuses.includes(p.status) ? 700 : 400, verticalAlign: "baseline"}}>
                                    {p.text}
                                </Link>
                            </Tooltip>
                        </React.Fragment>
                    ))}
                    {silent.wasted && <> · <Box component="span" sx={{color: "text.primary"}}>{fmtCompact(silent.wasted)} searches wasted yesterday</Box></>}
                </Typography>
            )}
        </Paper>
    );
};

export default FeedPanel;
